import * as Common from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  MultiSelect,
  type MultiSelectOption,
  type OnChangeRequest,
} from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';

import { useLabelsQuery } from '../../graphql/hooks/useLabelsQuery';
import { useAppContext } from '../../hooks/useAppContext';
import type { ParticipantNotesFilter } from '../../hooks/useParticipantNotesFilter';

interface Props {
  filter: ParticipantNotesFilter;
}

const ParticipantNoteContentFilterControl = (props: Props) => {
  const [value, setValue] = React.useState(props.filter.contentFilter);

  const onChange = React.useCallback(
    (value: string) => {
      setValue(value);
      props.filter.onContentFilter(value);
    },
    [props.filter],
  );

  React.useEffect(() => {
    setValue(props.filter.contentFilter);
  }, [props.filter.contentFilter]);

  return (
    <ChakraV3.Input
      onChange={(e) => onChange(e.target.value)}
      value={value}
      placeholder='All content'
      size='md'
    />
  );
};
const ParticipantNoteLabelFilterControl = (props: Props) => {
  const { organization } = useAppContext();
  const { data, isLoading } = useLabelsQuery({
    organizationId: organization.id,
    type: Common.LabelType.ParticipantNote,
  });

  const options: MultiSelectOption<Common.Label>[] = React.useMemo(() => {
    const labels = (data?.organization?.labels as Common.Label[]) ?? [];
    return labels.map((label) => {
      return {
        label: label.name,
        value: label.id,
        raw: label,
        color: label.color,
      };
    });
  }, [data?.organization?.labels]);

  const selectedValues: MultiSelectOption<Common.Label>[] = React.useMemo(() => {
    return (props.filter.labelFilter ?? []).flatMap((label) => {
      return options.find((o) => P.isEqual(o.raw.id, label)) ?? [];
    });
  }, [props.filter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<Common.Label>) => {
      props.filter.onLabelFilter(req.values.map(({ raw }) => raw.id));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      isLoading={isLoading}
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='md'
      placeholder='All labels'
      optionVariant='tag'
    />
  );
};

export const ParticipantNotesFilterControls = (props: Props) => {
  return (
    <>
      <ChakraV3.Stack maxW='2xs'>
        <ParticipantNoteContentFilterControl filter={props.filter} />
      </ChakraV3.Stack>

      <ChakraV3.Stack w='2xs'>
        <ParticipantNoteLabelFilterControl filter={props.filter} />
      </ChakraV3.Stack>
    </>
  );
};
